/** Widget types available in the builder. */

export const WIDGET_CATEGORIES = [
  {
    id: 'match',
    label: 'Match',
    types: [
      { id: 'match-card', label: 'Match Card', description: 'Score, venue, goals, cards, coaches' },
      { id: 'match-info', label: 'Match Info', description: 'Venue, attendance, referee, coaches' },
      { id: 'goals-list', label: 'Goals List', description: 'Goal scorers for a selected match' },
      { id: 'match-events', label: 'Match Events', description: 'Goals, cards, subs, VAR timeline' },
      { id: 'lineups', label: 'Lineups', description: 'Formations, XI, bookings, subs' },
      { id: 'coach-card', label: 'Coach Card', description: 'Head coaches for a match' },
    ],
  },
  {
    id: 'live',
    label: 'Live & schedule',
    types: [
      { id: 'live-scores', label: 'Live Scores', description: 'Mixed live, upcoming & results' },
      { id: 'live-matches', label: 'Live Now', description: 'In-play matches only' },
      { id: 'fixtures', label: 'Fixtures', description: 'Upcoming kickoffs' },
      { id: 'results', label: 'Results', description: 'Finished match scores' },
    ],
  },
  {
    id: 'league',
    label: 'League table',
    types: [
      { id: 'standings', label: 'Standings', description: 'Overall, home & away tables' },
      { id: 'group-standings', label: 'Group Tables', description: 'Tournament group stages' },
      { id: 'form-guide', label: 'Form Guide', description: 'Recent form string per team' },
      { id: 'team-stats', label: 'Goals For/Against', description: 'Attack & defence from table' },
    ],
  },
  {
    id: 'team',
    label: 'Team',
    types: [
      {
        id: 'team-list',
        label: 'Team List',
        description: 'All clubs in the league with table position & qualification status',
      },
      {
        id: 'team-profile',
        label: 'Team Profile',
        description: 'One club — stats, form, Champions League / Europa / relegation zone',
      },
    ],
  },
  {
    id: 'player',
    label: 'Player',
    types: [
      {
        id: 'player-list',
        label: 'Player List',
        description: 'Full squad for one club — age, nationality, position & stats',
      },
      {
        id: 'player-profile',
        label: 'Player Profile',
        description: 'One player — goals, assists, appearances',
      },
    ],
  },
  {
    id: 'stats',
    label: 'Player stats',
    types: [
      { id: 'top-scorers', label: 'Top Scorers', description: 'Goals leaderboard' },
      { id: 'top-assists', label: 'Top Assists', description: 'Assist leaderboard' },
      { id: 'appearances', label: 'Appearances', description: 'Most games played' },
      { id: 'minutes', label: 'Minutes Played', description: 'Most minutes on pitch' },
      { id: 'discipline', label: 'Cards', description: 'Yellow & red card totals' },
      { id: 'injuries', label: 'Injuries', description: 'Players marked injured in squads' },
    ],
  },
];

export const WIDGET_TYPES = WIDGET_CATEGORIES.flatMap((c) => c.types);

/** Builder UI: category + widget type labels from interface language. */
export function getTranslatedWidgetCategories(t) {
  return WIDGET_CATEGORIES.map((cat) => ({
    id: cat.id,
    label: t(`cat_${cat.id}`),
    types: cat.types.map((wt) => ({
      id: wt.id,
      label: t(`wt_${wt.id.replace(/-/g, '_')}_label`),
      description: t(`wt_${wt.id.replace(/-/g, '_')}_desc`),
    })),
  }));
}

/** Legacy slugs from saved widgets */
const TYPE_ALIASES = {
  'team-card': 'team-profile',
  'player-card': 'player-profile',
};

export function normalizeWidgetType(type) {
  return TYPE_ALIASES[type] || type;
}

export function normalizeWidgetConfig(config) {
  if (!config || typeof config !== 'object') return config;
  return {
    ...config,
    type: normalizeWidgetType(config.type),
  };
}

/** All widget type slugs — keep server/policy/planPolicy.js in sync */
export const ALL_WIDGET_TYPE_IDS = WIDGET_TYPES.map((w) => w.id);

export const FEATURED_MATCH_TYPES = new Set([
  'match-card',
  'match-info',
  'goals-list',
  'match-events',
  'lineups',
  'coach-card',
]);

export const TEAM_PICKER_TYPES = new Set(['team-profile', 'injuries', 'player-list']);
export const PLAYER_PICKER_TYPES = new Set(['player-profile']);

export const MOCK_COMPETITIONS = [
  { id: 'pl', label: 'Premier League', curated: true },
  { id: 'ucl', label: 'Champions League', curated: true },
  { id: 'laliga', label: 'La Liga', curated: true },
  { id: 'bundesliga', label: 'Bundesliga', curated: true },
  { id: 'serie-a', label: 'Serie A', curated: true },
  { id: 'world-cup', label: 'FIFA World Cup', curated: true },
];

export const THEMES = [
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
];

export const DEFAULT_WIDGET_CONFIG = {
  name: 'My widget',
  type: 'match-card',
  sport: 'football',
  competitionId: 'pl',
  locale: 'en',
  theme: 'dark',
  primaryColor: '16a34a',
  allowedDomains: [],
  featuredMatchId: '',
  selectedTeam: '',
  selectedPlayer: '',
};
